/**
 * Отправка комплекта файлов на сервер пачками — с повторами, прогрессом и
 * отменой. Общая часть для разделов, где за раз грузят сотни файлов.
 *
 * Правила повторов — те же, что у загрузки РД архивом (`pages/Pto/rdImport`):
 * повторяем только то, где повтор может помочь (нет связи, таймаут, 5xx, 408,
 * 429), до трёх попыток с растущей паузой; остальное — сразу ошибка. Сервер
 * обязан принимать повтор пачки без дублей (у договоров — дедуп по sha256).
 *
 * Пачки небольшие (по умолчанию ~32 МБ): тело запроса бэкенд держит в памяти
 * целиком, и обрыв на 80-мегабайтной пачке стоил бы её повторной заливки.
 */

/** HTTP-код ответа или код RTK Query (`FETCH_ERROR`, `TIMEOUT_ERROR`, …). */
export function httpStatus(err: unknown): number | string | undefined {
  return (err as { status?: number | string } | null | undefined)?.status;
}

/** Ошибка — отмена запроса или чтения (кнопка «Прервать», уход со страницы). */
export function isAbortError(err: unknown): boolean {
  return (err as { name?: unknown } | null | undefined)?.name === 'AbortError';
}

/** Сколько раз пробуем отправить одно и то же. */
export const MAX_ATTEMPTS = 3;

/** Сбой, при котором повтор может помочь: нет связи, 5xx, 408, 429. */
export function retryable(status: number | string | undefined): boolean {
  return (
    status === 'FETCH_ERROR' ||
    status === 'TIMEOUT_ERROR' ||
    status === 408 ||
    status === 429 ||
    (typeof status === 'number' && status >= 500)
  );
}

/** Пауза перед повтором `attempt` (1, 2, …): 2 с, 8 с; при 429 — 5 с, 20 с. */
export function backoff(status: number | string | undefined, attempt: number): number {
  return (status === 429 ? 5000 : 2000) * attempt * attempt;
}

/** Пауза, которую отмена обрывает сразу. */
export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal?.addEventListener('abort', done, { once: true });
  });
}

/** Целевой размер пачки по умолчанию. */
export const DEFAULT_BATCH_BYTES = 32 * 1024 * 1024;
/** Файлов в пачке не больше — мелкие сканы иначе собрались бы в пачку на тысячу. */
export const DEFAULT_BATCH_FILES = 50;

/**
 * Пачки по порядку: не больше `targetBytes` и `maxFiles`, кроме файла, который
 * сам крупнее `targetBytes`, — он уходит один. Порядок сохраняется (первый
 * файл комплекта — основной документ, ему ехать первым).
 */
export function planBatches<T extends { size: number }>(
  items: readonly T[],
  targetBytes = DEFAULT_BATCH_BYTES,
  maxFiles = DEFAULT_BATCH_FILES,
): T[][] {
  const batches: T[][] = [];
  let current: T[] = [];
  let bytes = 0;
  for (const item of items) {
    if (current.length > 0 && (bytes + item.size > targetBytes || current.length >= maxFiles)) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(item);
    bytes += item.size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

export interface BatchProgress {
  /** Отправлено пачек / всего. */
  batchesDone: number;
  batches: number;
  /** Отправлено файлов / всего. */
  filesDone: number;
  files: number;
  bytesDone: number;
  bytesTotal: number;
  /** Идёт повтор пачки после сбоя (номер попытки), иначе null. */
  retry: number | null;
}

export interface BatchResult {
  /** Сколько пачек ушло. */
  sent: number;
  /** Ошибка, на которой отправка остановилась (после повторов), иначе null. */
  error: unknown;
  aborted: boolean;
}

/**
 * Отправить пачки по очереди. `send(batch, index, total, signal)` — один
 * запрос (бросает ошибку RTK Query / `AbortError`). Ошибка, при которой повтор
 * бессмыслен, или исчерпанные попытки — отправка останавливается, ошибка в
 * результате (вызывающий решает, что сказать человеку). `canRetry(index)` —
 * можно ли повторять пачку: запрос, который что-то создаёт (первая пачка
 * договора), повторять нельзя — при обрыве после ответа сервера создалось бы
 * два договора.
 */
export async function sendBatches<T extends { size: number }>(
  batches: readonly T[][],
  send: (batch: T[], index: number, total: number, signal: AbortSignal) => Promise<void>,
  onProgress: (p: BatchProgress) => void,
  signal: AbortSignal,
  canRetry: (index: number) => boolean = () => true,
): Promise<BatchResult> {
  const files = batches.reduce((n, b) => n + b.length, 0);
  const bytesTotal = batches.reduce((n, b) => n + b.reduce((s, x) => s + x.size, 0), 0);
  let filesDone = 0;
  let bytesDone = 0;
  const progress = (batchesDone: number, retry: number | null) =>
    onProgress({
      batchesDone,
      batches: batches.length,
      filesDone,
      files,
      bytesDone,
      bytesTotal,
      retry,
    });
  progress(0, null);
  for (let i = 0; i < batches.length; i += 1) {
    const batch = batches[i]!;
    for (let attempt = 1; ; attempt += 1) {
      if (signal.aborted) return { sent: i, error: null, aborted: true };
      try {
        await send(batch, i, batches.length, signal);
        break;
      } catch (e) {
        if (signal.aborted || isAbortError(e)) return { sent: i, error: null, aborted: true };
        const status = httpStatus(e);
        if (!canRetry(i) || !retryable(status) || attempt >= MAX_ATTEMPTS) {
          return { sent: i, error: e, aborted: false };
        }
        progress(i, attempt + 1);
        await sleep(backoff(status, attempt), signal);
      }
    }
    filesDone += batch.length;
    bytesDone += batch.reduce((s, x) => s + x.size, 0);
    progress(i + 1, null);
  }
  return { sent: batches.length, error: null, aborted: false };
}
